import '../../vehicles/vehicle-add-edit/vehicle-add-edit.scss';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { observer } from 'mobx-react-lite';
import { Button, DatePicker, Divider, Form, Input, Select, SelectProps, Space } from 'antd';
import { useMedia } from 'common/hooks/use-media/use-media.hook';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { AddEditPageHeader } from 'features/eiq-connect/components/add-edit-page-header/add-edit-page-header';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import eiqConnectStore from 'features/eiq-connect/store/eiq-connect.store';
import residentsService from 'common/services/residents-service/residents.service';
import { AddEditActions } from 'features/eiq-connect/components/add-edit-actions/add-edit-actions';
import { DATE_SHORT_MMM_DD_YYYY } from 'common/constans/date.constants';
import { notification } from 'common/utils/notification';
import { IPetRead } from 'common/services/residents-service/residents.service.types';
import { StrToDate } from 'common/helpers/date.helper';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { formatAddressLabel } from 'common/helpers/address.helper';
import FileService from 'common/services/file-service/file.service';
import { IAddEditPetProps, IPetFormData } from './pet-add-edit.types';
import petService from 'common/services/pet-service/pet-service';
import { toCreatePetModel, toUpdatePetModel } from './pet-add-edit.helpers';
import { addEditPetValidation } from 'common/validation/pet.validation';
import PetDeleteModal from '../pet-delete-modal/pet-delete-modal';
import { IConfirmDeletePetProps } from '../pet-delete-modal/pet-delete-modal.types';
import { ResidentTypesEnum } from 'common/enums/resident-types.enum';
import { trackPromise } from 'react-promise-tracker';
import { UploadPhoto } from 'common/components/photo-upload/upload-photo';
import { useUploadPhoto } from 'common/hooks/use-upload-photo/use-upload-photo';

const initData: IPetFormData = {
  name: null,
  type: null,
  breed: null,
  color: null,
  dateOfBirth: null,
  tag: null,
  tagExpirationDate: null,
  chipNumber: null,
  addressId: null,
  photoUrl: null,
};

export const PetAddEdit = observer(({ pageType }: IAddEditPetProps) => {
  const [form] = Form.useForm();
  const { isMobileOrTable } = useMedia();
  const { id } = useParams();
  const navigate = useNavigate();
  const [addressOptions, setAddressOptions] = useState<SelectProps['options']>([]);
  const [currentPet, setCurrentPet] = useState<IPetRead>();
  const isFormSubmittedSuccessful = useRef(false);

  useEffect(() => {
    return () => {
      if (!isFormSubmittedSuccessful.current) {
        notification.destroy();
      }
    };
  }, []);

  const getAddresses = useCallback(() => {
    const addresses = eiqConnectStore?.residentDetails?.addresses || [];

    return addresses.filter((address) => address.ownershipType !== ResidentTypesEnum.Developer);
  }, []);

  useEffect(() => {
    if (!eiqConnectStore.residentDetails) {
      return;
    }

    const addressOptions = getAddresses().map((item) => ({
      value: item.address.id,
      label: formatAddressLabel(item.address),
    }));

    setAddressOptions(addressOptions || []);

    if (pageType === 'EDIT') {
      const pet = eiqConnectStore.residentDetails.addresses.flatMap((item) => item.pets).find((item) => item.id === Number(id!));
      setCurrentPet(pet);
    }
  }, [eiqConnectStore.residentDetails, getAddresses]);

  useEffect(() => {
    if (!form || !currentPet) {
      return;
    }

    const addressId = eiqConnectStore.residentDetails?.addresses?.find((item) => item.pets.includes(currentPet))?.address.id;

    const formData: IPetFormData = {
      name: currentPet.name,
      type: currentPet.type,
      breed: currentPet.breed,
      color: currentPet.color,
      dateOfBirth: StrToDate(currentPet.dateOfBirth),
      tag: currentPet.tag,
      tagExpirationDate: StrToDate(currentPet.tagExpirationDate),
      chipNumber: currentPet.chipNumber,
      addressId: addressId!,
      photoUrl: currentPet.photoUrl,
    };

    form.setFieldsValue(formData);
  }, [currentPet, form]);

  const updateResidentData = () => {
    residentsService
      .getResidentDetails(userAuthStore.webGuestResidentId!)
      .then((data) => {
        eiqConnectStore.setResidentDetails(data);
        navigate('/eiq-connect/pets');
      })
      .catch((e) => {
        notification.error({ message: 'An error occurred while getting resident details' });
      });
  };

  const currentAddressId = useMemo(() => {
    if (!currentPet) {
      return null;
    }

    return eiqConnectStore.residentDetails?.addresses?.find((item) => item.pets.includes(currentPet!))?.address.id;
  }, [currentPet]);

  const createPet = (residentId: number, values: IPetFormData) => {
    const addressId = addressOptions?.length === 1 ? addressOptions[0].value : values.addressId;

    trackPromise(
      petService
        .createPet(residentId, Number(addressId), toCreatePetModel(values))
        .then(() => {
          isFormSubmittedSuccessful.current = true;
          updateResidentData();
          notification.success({ message: 'The new pet has been successfully added' });
        })
        .catch(() => {
          notification.error({ message: 'An error occurred while updating a pet. Please try again.' });
        }),
    );
  };

  const updatePet = (residentId: number, values: IPetFormData) => {
    trackPromise(
      petService
        .updatePet(residentId, currentAddressId!, currentPet!.id, toUpdatePetModel(values, currentPet!))
        .then(() => {
          isFormSubmittedSuccessful.current = true;
          updateResidentData();
          notification.success({ message: 'The pet has been successfully updated' });
        })
        .catch((e) => {
          notification.error({ message: 'An error occurred while adding a pet. Please try again.' });
        }),
    );
  };

  const uploadText = (
    <p className="ant-upload-text">
      <span>{currentPet?.photoUrl ? 'Update' : 'Add'}</span> a photo of your pet
    </p>
  );

  const useUploadHock = useUploadPhoto({ currentUrl: currentPet?.photoUrl });

  const handleOnFinish = async (values: IPetFormData) => {
    const residentId: number = userAuthStore.webGuestResidentId!;

    const file = await useUploadHock.getFile();

    if (file) {
      const photoUrl = await FileService.uploadPhoto(file);
      values.photoUrl = photoUrl.absoluteUri;
    } else {
      values.photoUrl = '';
    }

    if (pageType === 'ADD') {
      createPet(residentId, values);
    } else {
      updatePet(residentId, values);
    }
  };

  const deletePetModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const residentId: number | null = userAuthStore.webGuestResidentId!;
      const data: IConfirmDeletePetProps = {
        pet: currentPet!,
        residentId: residentId,
        addressId: currentAddressId!,
      };

      setModalInitData(data);
    },
    onClose: (isSuccess) => {
      if (isSuccess) {
        updateResidentData();
        notification.success({ message: 'The pet has been successfully deleted' });
      }
    },
  });

  return (
    <FormWrapper
      form={form}
      onFinish={handleOnFinish}
      initialValues={initData}
      layout="vertical"
      className="eiq-vehicle-add-edit eiq-add-edit-page">
      <AddEditPageHeader isMobileOrTablet={isMobileOrTable} pageType={pageType} />
      <div className="eiq-content">
        {pageType === 'ADD' && addressOptions && addressOptions.length > 1 && (
          <Space className="full-width">
            <div className="eiq-car-block">
              <Form.Item name="addressId" label="Address" rules={addEditPetValidation.addressId}>
                <Select options={addressOptions} placeholder="Select address" />
              </Form.Item>
              <Divider className="account-diver" />
            </div>
          </Space>
        )}

        <Space className="full-width">
          <div className="eiq-car-block">
            <Form.Item name="name" label="Name" rules={addEditPetValidation.name}>
              <Input name="name" placeholder="Enter name" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width">
          <div className="eiq-car-block">
            <Form.Item name="type" label="Type">
              <Input placeholder="Enter type" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <div className="eiq-car-block">
            <Form.Item name="breed" label="Breed" rules={addEditPetValidation.breed}>
              <Input placeholder="Enter breed" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width">
          <div className="eiq-car-block">
            <Form.Item name="color" label="Color" rules={addEditPetValidation.color}>
              <Input placeholder="Enter color" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <div className="eiq-car-block">
            <Form.Item name="dateOfBirth" label="Date of birth">
              <DatePicker className="full-width" placeholder="Date of birth" format={DATE_SHORT_MMM_DD_YYYY} />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width">
          <div className="eiq-car-block">
            <Form.Item name="tag" label="Tag">
              <Input placeholder="Enter tag" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <div className="eiq-car-block">
            <Form.Item name="tagExpirationDate" label="Shots expiration date">
              <DatePicker className="full-width" placeholder="Shots expiration date" format={DATE_SHORT_MMM_DD_YYYY} />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width ant-space-with-uploader">
          <div className="eiq-car-block">
            <Form.Item name="chipNumber" label="Chip number">
              <Input placeholder="Enter chip number" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <UploadPhoto hook={useUploadHock} uploadText={uploadText} />
        </Space>

        {pageType === 'EDIT' && (
          <Button type="link" onClick={deletePetModal.showModal}>
            Delete pet
          </Button>
        )}
      </div>

      {isMobileOrTable && (
        <div className="eiq-footer-actions">
          <AddEditActions type={pageType} />
        </div>
      )}
      {deletePetModal.isOpenModal && (
        <PetDeleteModal
          isEdit={false}
          initData={deletePetModal?.initData}
          title="Delete"
          isOpen={deletePetModal?.isOpenModal}
          onClose={deletePetModal?.hideModal}
        />
      )}
    </FormWrapper>
  );
});
